import { useMemo, useState } from 'react';
import { Customer } from '~/lib/api/smartsend/types';
import ValidateCustomerAccountDetails from '../../smartsend/onboarding/ValidateCustomerAccountDetails';
import { useTranslation } from 'react-i18next';
import { Individual, NotRegistered, Registered } from '~/apps/smartsend/lib/consts/TaxStatusConsts';
import { TranslationNamespaces } from '~/lib/constants';
import {
  P2GBox,
  P2GButton,
  P2GContainer,
  P2GGrid,
  P2GLink,
  P2GLinkGroup,
  P2GText,
  P2GTextField
} from '@parcel2go/core-ui';
import { TaxId } from '~/apps/smartsend/lib/consts/ItemMetaConstants';
import AddressTypeahead from '../../address/typeahead';
import { AppCard } from '~/components/card/app-card';
import DebouncedInput from '../../DebouncedInput';
import useMapAddressLookup from '../../address/MapAddressLookupToCustomer';
import useDefaultCustoms from '~/apps/saved-addresses/use-default-customs';
import { CustomerTaxDetails } from '../../smartsend/CustomerTaxDetails';

interface props {
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  isXs: boolean;
  creating: boolean;
  initialCustomer: Customer;
  finish: (customer: Customer, link: boolean) => void;
  intro?: React.ReactNode;
  buttonWrapper?: (children: React.ReactNode) => React.ReactNode;
}

export function AddCollectionAddress(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [view, setView] = useState<'overview' | 'edit' | 'search'>('search');
  const [address, setAddress] = useState<Customer>({ ...props.initialCustomer });
  const { mapAddressLookupToCustomer } = useMapAddressLookup();

  const defaultCustoms = useDefaultCustoms(address?.CountryIsoCode);
  const validation = useMemo(() => {
    const rules = (() => {
      // Show all the fields when they've entered a company name.
      if (address?.Organisation) {
        return defaultCustoms;
      }

      // Show TAX ID field for Spain on signup all the time.
      if (address?.CountryIsoCode === 'ESP') {
        return defaultCustoms.filter((x) => x.key === TaxId);
      }

      // Otherwise, hide all the conditional fields until they enter a company name to avoid overwhelming them.
      return [];
    })();

    return rules.map((x) => ({
      key: x.key,
      name: x.key,
      required: false,
      label: x.label,
      value: address.Meta[x.key]?.Value ?? address[x.key],
      pattern: x.validation
    }));
  }, [defaultCustoms, address]);

  const isValid = useMemo<boolean>(
    () =>
      ValidateCustomerAccountDetails(address) &&
      !!address.AccountName &&
      !!address.Town &&
      !!address.AddressLine1 &&
      !!address.AddressLine2 &&
      !!address.Postcode,
    [address]
  );

  const setDetails = (field: string, val: string) => {
    const i = { ...address };
    i[field] = val;

    if (field === 'Organisation') {
      i.VatStatus = val ? (i.VatNumber ? Registered : NotRegistered) : Individual;

      // No company, no company VAT number
      if (!val) {
        i.VatNumber = '';
      }
    }

    setAddress(i);
  };

  function setRecipientDetails(field: string, val: string) {
    const i = { ...address };
    i[field] = val;
    setAddress(i);
  }

  if (view === 'search') {
    return (
      <P2GContainer maxWidth={props.maxWidth}>
        <P2GBox style={{ minHeight: '400px' }}>
          <AddressTypeahead
            required={true}
            onSelect={async (c) => {
              const updated = await mapAddressLookupToCustomer(address, c, 'Sender', address.AccountName || 'Default');
              setAddress(updated);
              setView('overview');
            }}
            countryIsoCode={address.CountryIsoCode}
            search={address.Postcode}
          />
          <P2GBox mt={2}>
            <P2GText>
              {t('ADDRESS_TYPEAHEAD_HELPER_TEXT', 'Start typing your house number, street and postcode.')}
            </P2GText>
          </P2GBox>
        </P2GBox>
      </P2GContainer>
    );
  }

  return (
    <>
      <P2GContainer maxWidth={props.maxWidth}>
        <P2GGrid container spacing={2}>
          {view === 'overview' && (
            <P2GGrid item xs={12}>
              <AppCard>
                <P2GBox p={2} display="flex" flexDirection="column">
                  <P2GText>
                    <strong>{address.Name}</strong>
                  </P2GText>
                  <P2GText>
                    {address.AddressLine1} {address.AddressLine2}
                  </P2GText>
                  <P2GText>
                    {address.Town}, {address.Postcode}
                  </P2GText>
                  <P2GText mt={1} variant="body2">
                    <i>{address.Email}</i>
                  </P2GText>
                </P2GBox>
              </AppCard>
            </P2GGrid>
          )}

          {view === 'edit' && (
            <>
              <P2GGrid item xs={6}>
                <P2GTextField
                  fullWidth
                  value={address.AddressLine1}
                  required
                  onChange={(v) => {
                    setRecipientDetails('AddressLine1', v.currentTarget.value);
                  }}
                  label="Property"
                />
              </P2GGrid>
              <P2GGrid item xs={6}>
                <P2GTextField
                  fullWidth
                  value={address.AddressLine2}
                  required
                  onChange={(v) => {
                    setRecipientDetails('AddressLine2', v.currentTarget.value);
                  }}
                  label="Street"
                />
              </P2GGrid>
              <P2GGrid item xs={6}>
                <P2GTextField
                  fullWidth
                  value={address.Postcode}
                  required
                  onChange={(v) => {
                    setRecipientDetails('Postcode', v.currentTarget.value);
                  }}
                  label="Postcode"
                />
              </P2GGrid>
              <P2GGrid item xs={6}>
                <P2GTextField
                  fullWidth
                  value={address.Town}
                  required
                  onChange={(v) => {
                    setRecipientDetails('Town', v.currentTarget.value);
                  }}
                  label="Town"
                />
              </P2GGrid>
              <P2GGrid item xs={6}>
                <P2GTextField
                  fullWidth
                  value={address.County}
                  required
                  onChange={(v) => {
                    setRecipientDetails('County', v.currentTarget.value);
                  }}
                  label="County"
                />
              </P2GGrid>
            </>
          )}

          <P2GGrid item xs={12}>
            <P2GLinkGroup
              links={[
                view === 'overview' && (
                  <P2GLink key="edit" onClick={() => setView('edit')}>
                    {t('ADDRESS_TYPEAHEAD_EDIT', 'Edit Address')}
                  </P2GLink>
                ),
                <P2GLink
                  key="search-again"
                  onClick={() => {
                    setAddress({ ...props.initialCustomer });
                    setView('search');
                  }}
                >
                  {t('ADDRESS_TYPEAHEAD_SEARCH_AGAIN', 'Search Again')}
                </P2GLink>
              ]}
            />
          </P2GGrid>

          {!props.initialCustomer.Name && (
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                value={address.Name}
                required
                onChange={(v) => setDetails('Name', v.currentTarget.value)}
                label={t('FULL_NAME_INPUT_LABEL', { defaultValue: 'Full Name' })}
                id="Name"
              />
            </P2GGrid>
          )}

          {!props.initialCustomer.Email && (
            <P2GGrid item xs={12}>
              <P2GTextField
                fullWidth
                type="email"
                required
                value={address.Email}
                onChange={(v) => setDetails('Email', v.currentTarget.value)}
                label={t('EMAIL_ADDRESS_INPUT_LABEL', { defaultValue: 'Email Address' })}
                id="Email"
              />
            </P2GGrid>
          )}

          <P2GGrid item xs={12}>
            <P2GTextField
              fullWidth
              type="tel"
              required
              value={address.Phone}
              onChange={(v) => setDetails('Phone', v.currentTarget.value)}
              label={t('PHONE_NUMBER_INPUT_LABEL', { defaultValue: 'Phone Number' })}
              id="Phone"
            />
          </P2GGrid>

          <P2GGrid item xs={12}>
            <DebouncedInput
              fullWidth
              value={address.Organisation}
              onChange={(v) => setDetails('Organisation', v)}
              label={t('COMPANY_INPUT_LABEL', { defaultValue: 'Company Name' })}
              id="Organisation"
            />
          </P2GGrid>

          {validation.length > 0 && (
            <CustomerTaxDetails
              customer={address}
              onUpdate={setAddress}
              type="Sender"
              validation={validation}
              alternativeCustomer={null}
              includeTitle={false}
              wrapper={(x) => (
                <P2GGrid item xs={12}>
                  {x}
                </P2GGrid>
              )}
            />
          )}
        </P2GGrid>
      </P2GContainer>
      {props.buttonWrapper ? (
        props.buttonWrapper(
          <P2GButton
            size="large"
            fullWidth={props.isXs}
            variant="primary"
            disabled={!isValid}
            onClick={() => props.finish(address, false)}
          >
            {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
          </P2GButton>
        )
      ) : (
        <P2GBox display="flex" justifyContent="center" flexDirection="column" gap={2} mt={2}>
          <P2GButton size="large" variant="primary" disabled={!isValid} onClick={() => props.finish(address, false)}>
            {t('CONTINUE_BUTTON', { defaultValue: 'Continue' })}
          </P2GButton>
        </P2GBox>
      )}
    </>
  );
}
